/**
 * Types de notifications prédéfinis du backend
 * Correspond à l'enum NotificationType du backend
 */
import { resolveSoccerV2SportsRoutes } from '@/domains/soccer-v2/utils/soccer-v2-notification-display';
import { getLiveStreamIdFromNotification } from '@/domains/live/utils/liveNotificationUtils';

export const NOTIFICATION_TYPES = {
  // Messages
  NEW_MESSAGE: 'NEW_MESSAGE',
  MENTION: 'MENTION',
  REPLY: 'REPLY',
  CONVERSATION_CREATED: 'CONVERSATION_CREATED',
  USER_ADDED_TO_CONVERSATION: 'USER_ADDED_TO_CONVERSATION',
  USER_REMOVED_FROM_CONVERSATION: 'USER_REMOVED_FROM_CONVERSATION',
  REACTION_ADDED: 'REACTION_ADDED',
  MESSAGE_REACTION: 'MESSAGE_REACTION',

  // Social
  POST_LIKE: 'POST_LIKE',
  COMMENT: 'COMMENT',
  POST_SHARED: 'POST_SHARED',
  POST_TAGGED: 'POST_TAGGED',
  COMMENT_MENTION: 'COMMENT_MENTION',
  POST_UPDATED: 'POST_UPDATED',
  GROUP_TAGGED: 'GROUP_TAGGED',
  PAGE_TAGGED: 'PAGE_TAGGED',
  FRIEND_REQUEST: 'FRIEND_REQUEST',
  FRIEND_REQUEST_ACCEPTED: 'FRIEND_REQUEST_ACCEPTED',

  // Groups
  GROUP_INVITE_TO_GROUP: 'GROUP_INVITE_TO_GROUP',
  GROUP_JOIN_REQUEST: 'GROUP_JOIN_REQUEST',
  GROUP_JOIN_REQUEST_ACCEPTED: 'GROUP_JOIN_REQUEST_ACCEPTED',
  GROUP_POST_CREATED: 'GROUP_POST_CREATED',
  GROUP_MEMBER_ADDED: 'GROUP_MEMBER_ADDED',

  // Pages
  PAGE_FOLLOW: 'PAGE_FOLLOW',
  PAGE_MEMBER_ADDED: 'PAGE_MEMBER_ADDED',
  PAGE_POST_CREATED: 'PAGE_POST_CREATED',
  PAGE_INVITE_TO_FOLLOW: 'PAGE_INVITE_TO_FOLLOW',
  PAGE_INVITE_ACCEPTED: 'PAGE_INVITE_ACCEPTED',
  PAGE_MEMBER_ROLE_UPDATED: 'PAGE_MEMBER_ROLE_UPDATED',
  PAGE_MEMBER_INVITE_ACCEPTED: 'PAGE_MEMBER_INVITE_ACCEPTED',
  PAGE_MEMBER_INVITE_DECLINED: 'PAGE_MEMBER_INVITE_DECLINED',

  // Events
  EVENT_INVITATION: 'EVENT_INVITATION',
  EVENT_CANCELLED: 'EVENT_CANCELLED',
  EVENT_UPDATED: 'EVENT_UPDATED',
  EVENT_INVITATION_ACCEPTED: 'EVENT_INVITATION_ACCEPTED',
  // System
  INVITATION: 'INVITATION',
  SYSTEM_ANNOUNCEMENT: 'SYSTEM_ANNOUNCEMENT',
  BRONZE_BADGE: 'BRONZE_BADGE',
  SILVER_BADGE: 'SILVER_BADGE',
  GOLD_BADGE: 'GOLD_BADGE',
  VOICE_CALL: 'VOICE_CALL',
  VIDEO_CALL: 'VIDEO_CALL',

  // Birthdays
  BIRTHDAY_REMINDER: 'BIRTHDAY_REMINDER',
  // Todo/Tasks
  TASK_ASSIGNED: 'TASK_ASSIGNED',
  TASK_COMPLETED: 'TASK_COMPLETED',
  TASK_REOPENED: 'TASK_REOPENED',
  PROJECT_INVITATION: 'PROJECT_INVITATION',
  TASK_UPDATED: 'TASK_UPDATED',
  TASK_REMINDER_DAILY: 'TASK_REMINDER_DAILY',
  TASK_REMINDER_BEFORE_DEADLINE: 'TASK_REMINDER_BEFORE_DEADLINE',
  TASK_REMINDER_CUSTOM: 'TASK_REMINDER_CUSTOM',

  // Moderation
  POST_PENDING_MODERATION: 'POST_PENDING_MODERATION',
  POST_APPROVED: 'POST_APPROVED',
  POST_BANNED: 'POST_BANNED',
  STORY_PENDING_MODERATION: 'STORY_PENDING_MODERATION',
  STORY_APPROVED: 'STORY_APPROVED',
  STORY_BANNED: 'STORY_BANNED',
  COMMENT_PENDING_MODERATION: 'COMMENT_PENDING_MODERATION',
  COMMENT_APPROVED: 'COMMENT_APPROVED',
  COMMENT_BANNED: 'COMMENT_BANNED',

  // Stories
  STORY_TAGGED: 'STORY_TAGGED',
  STORY_GROUP_TAGGED: 'STORY_GROUP_TAGGED',
  STORY_PAGE_TAGGED: 'STORY_PAGE_TAGGED',
  STORY_REACTION: 'STORY_REACTION',

  // Social Features
  FAMILY_REQUEST: 'FAMILY_REQUEST',
  CAMPAIGN_FINISHED: 'CAMPAIGN_FINISHED',

  // Sports / Favorite Teams
  FAVORITE_TEAM_MATCH_STARTING: 'FAVORITE_TEAM_MATCH_STARTING',
  FAVORITE_TEAM_MATCH_STARTED: 'FAVORITE_TEAM_MATCH_STARTED',
  FAVORITE_TEAM_MATCH_FINISHED: 'FAVORITE_TEAM_MATCH_FINISHED',
  FAVORITE_TEAM_SCORED: 'FAVORITE_TEAM_SCORED',
  FAVORITE_TEAM_WON: 'FAVORITE_TEAM_WON',
  FAVORITE_TEAM_LOST: 'FAVORITE_TEAM_LOST',
  FAVORITE_TEAM_DREW: 'FAVORITE_TEAM_DREW',
  FAVORITE_TEAM_MATCH_REMINDER: 'FAVORITE_TEAM_MATCH_REMINDER',

  // Legacy / divers
  MESSAGE: 'MESSAGE',
  
  // Live
  USER_WENT_LIVE: 'USER_WENT_LIVE'
};

/**
 * Catégories de notifications pour le filtrage
 */
export const NOTIFICATION_CATEGORIES = {
  CHAT: 'chat',
  SOCIAL: 'social',
  MESSAGE: 'message',
  INVITATION: 'invitation',
  FRIENDSHIP: 'friendship',
  GROUP: 'group',
  ACHIEVEMENT: 'achievement',
  PAGE: 'page',
  CALL: 'call',
  SYSTEM: 'system',
  TODO: 'todo',
  OTHER: 'other'
};

/**
 * Priorités des notifications
 */
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
};

const toArray = (value) => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const resolvePostRoutes = (notification) => {
  const postId =
    notification?.postId ||
    notification?.post?.id ||
    notification?.comment?.postId ||
    notification?.comment?.post?.id ||
    notification?.data?.postId ||
    notification?.data?.post?.id ||
    notification?.data?.comment?.postId;

  if (!postId) {
    return null;
  }

  // Special handling for COMMENT_MENTION to include commentId in query
  if (notification?.type === 'COMMENT_MENTION') {
    const commentId = 
      notification?.data?.commentId ||
      notification?.commentId ||
      notification?.comment?.id;
    
    if (commentId) {
      return [
        { name: 'social.post-detail', params: { postId }, query: { commentId } },
        `/post/${postId}?commentId=${commentId}`,
        `/feed?post=${postId}&commentId=${commentId}`
      ];
    }
  }

  return [
    { name: 'social.post-detail', params: { postId } },
    `/post/${postId}`,
    `/feed?post=${postId}`
  ];
};

const resolveConversationRoutes = (notification) => {
  const conversationId =
    notification?.conversationId ||
    notification?.conversation?.id ||
    notification?.data?.conversationId ||
    notification?.message?.conversationId ||
    notification?.message?.conversation?.id;

  if (!conversationId) {
    return [{ name: 'chat.chat' }, '/chat'];
  }

  return [
    { name: 'chat.chat', params: { conversationId } },
    `/chat/${conversationId}`
  ];
};

const resolveMessageRoutes = (notification) => {
  const messageId =
    notification?.messageId ||
    notification?.message?.id ||
    notification?.data?.messageId;

  if (!messageId) {
    return resolveConversationRoutes(notification);
  }

  return resolveConversationRoutes(notification);
};

const resolveGroupRoutes = (notification) => {
  // Try to find groupId from various possible locations in the notification object
  const groupId =
    notification?.groupId ||
    notification?.group?.id ||
    notification?.data?.groupId ||
    notification?.data?.group?.id ||
    notification?.data?.invite?.groupId ||
    notification?.data?.invite?.group?.id ||
    notification?.data?.invitation?.groupId ||
    notification?.data?.invitation?.group?.id;

  console.debug('[NotificationNav] resolveGroupRoutes - notification:', notification, 'resolved groupId:', groupId);

  if (!groupId) {
    return [{ name: 'social.group' }];
  }

  // For GROUP_JOIN_REQUEST, navigate to requests tab
  if (notification?.type === 'GROUP_JOIN_REQUEST') {
    return [
      { name: 'social.group-detail', params: { groupId }, query: { tab: 'requests' } },
      { name: 'social.group-detail', params: { groupId } },
      { name: 'social.group', query: { focus: groupId } }
    ];
  }

  // For GROUP_POST_CREATED with pending approval, navigate to unapproved-posts tab
  if (notification?.type === 'GROUP_POST_CREATED' && notification?.data?.pendingApproval) {
    return [
      { name: 'social.group-detail', params: { groupId }, query: { tab: 'unapproved-posts' } },
      { name: 'social.group-detail', params: { groupId } },
      { name: 'social.group', query: { focus: groupId } }
    ];
  }

  return [
    { name: 'social.group-detail', params: { groupId } },
    { name: 'social.group', query: { focus: groupId } }
  ];
};

const resolveEventRoutes = (notification) => {
  // Pour EVENT_CANCELLED, rediriger vers la liste des événements
  if (notification?.type === 'EVENT_CANCELLED') {
    return [{ name: 'social.events-list' }];
  }

  // Pour les autres types d'événements, rediriger vers les détails
  // Priorité: data.eventId (comme dans la réponse API), puis eventId direct, puis event.id
  const eventId =
    notification?.data?.eventId ||
    notification?.eventId ||
    notification?.event?.id;

  if (!eventId) {
    return [{ name: 'social.events-list' }];
  }

  return [
    { name: 'social.event-detail', params: { id: eventId } },
  ];
};

const resolvePageRoutes = (notification) => {
  // Member invitation-like notification should open invitations tab.
  if (notification?.type === 'PAGE_MEMBER_ADDED') {
    return [{ name: 'social.pagelist', query: { tab: 'invites' } }];
  }

  const pageId =
    notification?.pageId ||
    notification?.page?.id ||
    notification?.data?.pageId;

  if (!pageId) {
    return [{ name: 'social.pagelist' }];
  }

  return [
    { name: 'social.pageview', params: { id: pageId } },
  ];
};

const resolvePageInvitationRoutes = (notification) => {
  return [
    { name: 'social.pagelist', query: { tab: 'invites' } }
  ];
};

const resolveProfileBadgeRoutes = () => [{ name: 'social.profilebadges' }];

const resolveBirthdayRoutes = () => [{ name: 'social.birthdays' }];

/**
 * Vérifie si une notification a moins de 24 heures
 * @param {Object} notification - Notification object avec createdAt
 * @returns {boolean} True si la notification a moins de 24h
 */
const isNotificationLessThan24Hours = (notification) => {
  if (!notification?.createdAt) return false;
  
  const notificationDate = new Date(notification.createdAt);
  const now = new Date();
  const diffMs = now - notificationDate;
  const diffHours = diffMs / (1000 * 60 * 60); // Convertir en heures
  
  return diffHours < 24;
};

/**
 * Resolves routes for story notifications
 * Navigates to the story owner's profile with the storyId param
 * @param {Object} notification - Notification object
 * @returns {Array} Possible routes
 */
const resolveStoryRoutes = (notification) => {
  const storyId =
    notification?.storyId ||
    notification?.story?.id ||
    notification?.data?.storyId ||
    notification?.data?.story?.id;

  const userId =
    notification?.actorId ||
    notification?.actor?.id;

  const groupId =
    notification?.groupId ||
    notification?.group?.id ||
    notification?.data?.groupId ||
    notification?.data?.group?.id;

  const pageId =
    notification?.pageId ||
    notification?.page?.id ||
    notification?.data?.pageId ||
    notification?.data?.page?.id;

  // Gestion spéciale pour STORY_TAGGED : vérifier l'âge de la notification
  if (notification?.type === 'STORY_TAGGED') {
    const isRecent = isNotificationLessThan24Hours(notification);
    
    if (userId) {
      if (isRecent && storyId) {
        // Notification récente (< 24h) : rediriger vers le profil avec storyId
        return [
          { name: 'social.friendprofile', params: { id: userId, storyId } }
        ];
      } else {
        // Notification ancienne (>= 24h) : rediriger vers le profil sans storyId
        return [
          { name: 'social.friendprofile', params: { id: userId } }
        ];
      }
    }
    
    // Fallback si pas d'userId
    return [{ name: 'social.friendprofile' }];
  }

  // Gestion spéciale pour STORY_REACTION : rediriger vers le profil de l'utilisateur connecté (propriétaire de la story)
  if (notification?.type === 'STORY_REACTION') {
    if (storyId) {
      // Rediriger vers le profil de l'utilisateur connecté avec le storyId
      return [
        { name: 'social.profilemain', params: { storyId } }
      ];
    }
    // Fallback si pas de storyId
    return [
      { name: 'social.profilemain' }
    ];
  }

  if (!storyId) {
    return null;
  }

  // Pour les stories de groupe
  if (groupId) {
    return [
      { name: 'social.group-detail', params: { groupId }, query: { storyId } },
      { name: 'social.group-detail', params: { groupId } }
    ];
  }

  // Pour les stories de page
  if (pageId) {
    return [
      { name: 'social.pageview', params: { id: pageId }, query: { storyId } },
      { name: 'social.pageview', params: { id: pageId } }
    ];
  }

  // Navigate to friend's profile with storyId to open the story
  if (userId) {
    return [
      { name: 'social.friendprofile', params: { id: userId, storyId } }
    ];
  }

  // Fallback: Navigate to own profile with storyId
  return [
    { name: 'social.profilemain', params: { storyId } }
  ];
};

/**
 * Résout les routes pour les tâches et projets
 * @param {Object} notification - Notification
 * @returns {Array} Routes possibles
 */
const resolveTodoRoutes = (notification) => {
  const taskId =
    notification?.taskId ||
    notification?.task?.id ||
    notification?.data?.taskId;

  const projectId =
    notification?.projectId ||
    notification?.project?.id ||
    notification?.data?.projectId ||
    notification?.task?.projectId;

  // Si on a un taskId, on peut naviguer vers la tâche spécifique
  if (taskId) {
    return [
      { name: 'app.todo', query: { taskId } }
    ];
  }

  // Si on a un projectId, on peut naviguer vers le projet
  if (projectId) {
    return [
      { name: 'app.todo', query: { projectId } }
    ];
  }

  // Par défaut, on va à la page todo
  return [{ name: 'app.todo' }];
};

/**
 * Résout les routes pour les notifications sportives (équipes favorites)
 * @param {Object} notification - Notification
 * @returns {Array} Routes possibles
 */
const resolveSportsRoutes = (notification) => resolveSoccerV2SportsRoutes(notification);

/**
 * Résout les routes pour les demandes de famille
 * @param {Object} notification - Notification
 * @returns {Array} Routes possibles
 */
const resolveFamilyRoutes = (notification) => {
  // Navigation vers la page d'édition de profil, onglet famille (manage-family)
  // Cela permet à l'utilisateur de gérer les demandes de famille directement
  return [
    { name: 'user.profile-edit', hash: '#manage-family' }
  ];
};

/**
 * Résout les routes pour les campagnes
 * @param {Object} notification - Notification
 * @returns {Array} Routes possibles
 */
const resolveCampaignRoutes = (notification) => {
  const campaignId =
    notification?.campaignId ||
    notification?.campaign?.id ||
    notification?.data?.campaignId ||
    notification?.data?.campaign?.id;

  if (campaignId) {
    // Si une route de campagne existe, l'utiliser
    return [
      { name: 'social.campaign', params: { id: campaignId } },
      `/campaign/${campaignId}`
    ];
  }

  // Fallback vers la page des notifications ou accueil
  return [
    { name: 'social.notification' },
    { name: 'social.feed' }
  ];
};

/**
 * Resolves routes for live stream notifications
 * @param {Object} notification - Notification
 * @returns {Array} Possible routes
 */
const resolveLiveRoutes = (notification) => {
  const liveStreamId = getLiveStreamIdFromNotification(notification);

  if (liveStreamId) {
    return [
      { name: 'live.viewer.screen', params: { id: liveStreamId } },
      { name: 'live.viewer', params: { id: liveStreamId } },
      `/live/${liveStreamId}`,
    ];
  }

  return [
    { name: 'live.feed' },
    '/live',
  ];
};

/**
 * Configuration des types de notifications
 * Définit les propriétés d'affichage et de comportement pour chaque type
 */
export const NOTIFICATION_TYPE_CONFIG = {
  [NOTIFICATION_TYPES.EVENT_INVITATION]: {
    icon: 'ph ph-calendar-plus',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation to an event',
    url: null,
    urlResolver: resolveEventRoutes
  },
  [NOTIFICATION_TYPES.EVENT_CANCELLED]: {
    icon: 'ph ph-x-circle',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: "L'événement a été annulé",
    url: null,
    urlResolver: resolveEventRoutes
  },
  [NOTIFICATION_TYPES.EVENT_UPDATED]: {
    icon: 'ph ph-pencil',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: "L'événement a été mis à jour",
    url: null,
    urlResolver: resolveEventRoutes
  },
  [NOTIFICATION_TYPES.EVENT_INVITATION_ACCEPTED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation à un événement acceptée',
    url: null,
    urlResolver: resolveEventRoutes
  },
  [NOTIFICATION_TYPES.NEW_MESSAGE]: {
    icon: 'ph ph-chat-circle',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.CHAT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Nouveau message reçu',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.MENTION]: {
    icon: 'ph ph-at',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Vous avez été mentionné',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.REPLY]: {
    icon: 'ph ph-arrow-bend-up-left',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Réponse à votre commentaire',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.CONVERSATION_CREATED]: {
    icon: 'ph ph-chat-circle-plus',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.CHAT,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: true,
    description: 'Nouvelle conversation créée',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.USER_ADDED_TO_CONVERSATION]: {
    icon: 'ph ph-user-plus',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.CHAT,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: true,
    description: 'Ajouté à une conversation',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.USER_REMOVED_FROM_CONVERSATION]: {
    icon: 'ph ph-user-minus',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.CHAT,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: true,
    description: "Retiré d'une conversation",
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.REACTION_ADDED]: {
    icon: 'ph ph-heart',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.LOW,
    autoRead: true,
    description: 'Réaction ajoutée',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.MESSAGE_REACTION]: {
    icon: 'ph ph-smiley',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.CHAT,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: true,
    description: 'Réaction sur votre message',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.POST_LIKE]: {
    icon: 'ph ph-heart',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.LOW,
    autoRead: true,
    description: 'Post aimé',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.COMMENT]: {
    icon: 'ph ph-chat-circle',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Nouveau commentaire',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.POST_SHARED]: {
    icon: 'ph ph-share-network',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Post partagé',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.POST_TAGGED]: {
    icon: 'ph ph-tag',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Vous avez été identifié sur un post',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.GROUP_TAGGED]: {
    icon: 'ph ph-tag',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Vous avez été identifié dans un post de groupe',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.PAGE_TAGGED]: {
    icon: 'ph ph-tag',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Vous avez été identifié dans un post de page',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.COMMENT_MENTION]: {
    icon: 'ph ph-at',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Mention dans un commentaire',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.POST_UPDATED]: {
    icon: 'ph ph-pencil',
    color: 'secondary',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.LOW,
    autoRead: true,
    description: 'Post mis à jour',
    url: { name: 'social.friendrequest' },
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.MESSAGE]: {
    icon: 'ph ph-envelope',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.MESSAGE,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Nouveau message',
    url: { name: 'social.friendrequest' },
    urlResolver: resolveMessageRoutes
  },
  [NOTIFICATION_TYPES.INVITATION]: {
    icon: 'ph ph-envelope-open',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.INVITATION,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Nouvelle invitation',
    url: { name: 'social.notification' }
  },
  [NOTIFICATION_TYPES.FRIEND_REQUEST]: {
    icon: 'ph ph-user-plus',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.FRIENDSHIP,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: "Nouvelle demande d'ami",
    url: { name: 'social.friendrequest' }
  },
  [NOTIFICATION_TYPES.FRIEND_REQUEST_ACCEPTED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.FRIENDSHIP,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: "Demande d'ami acceptée",
    url: { name: 'social.friendlist' }
  },
  [NOTIFICATION_TYPES.GROUP_INVITE_TO_GROUP]: {
    icon: 'ph ph-users-plus',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.GROUP,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation à rejoindre un groupe',
    url: null,
    urlResolver: resolveGroupRoutes
  },
  [NOTIFICATION_TYPES.GROUP_JOIN_REQUEST]: {
    icon: 'ph ph-user-plus',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.GROUP,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: "Nouvelle demande d'adhésion au groupe",
    url: null,
    urlResolver: resolveGroupRoutes
  },
  [NOTIFICATION_TYPES.GROUP_JOIN_REQUEST_ACCEPTED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.GROUP,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: "Demande d'adhésion au groupe acceptée",
    url: null,
    urlResolver: resolveGroupRoutes
  },
  [NOTIFICATION_TYPES.GROUP_POST_CREATED]: {
    icon: 'ph ph-megaphone',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.GROUP,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Nouveau post dans le groupe',
    url: null,
    urlResolver: resolveGroupRoutes
  },
  [NOTIFICATION_TYPES.GROUP_MEMBER_ADDED]: {
    icon: 'ph ph-user-plus',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.GROUP,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Nouveau membre ajouté au groupe',
    url: null,
    urlResolver: resolveGroupRoutes
  },
  [NOTIFICATION_TYPES.PAGE_FOLLOW]: {
    icon: 'ph ph-megaphone-simple',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.LOW,
    autoRead: false,
    description: 'Nouvelle page suivie',
    url: null
  },
  [NOTIFICATION_TYPES.PAGE_MEMBER_ADDED]: {
    icon: 'ph ph-user-circle-plus',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Ajouté à une page',
    url: null,
    urlResolver: resolvePageRoutes
  },
  [NOTIFICATION_TYPES.PAGE_POST_CREATED]: {
    icon: 'ph ph-newspaper-clipping',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Nouvelle publication sur la page',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.PAGE_INVITE_TO_FOLLOW]: {
    color: 'info',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation à suivre une page',
    url: null,
    urlResolver: resolvePageInvitationRoutes
  },
  [NOTIFICATION_TYPES.PAGE_INVITE_ACCEPTED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation à suivre une page acceptée',
    url: null,
    urlResolver: resolvePageRoutes
  },
  [NOTIFICATION_TYPES.PAGE_MEMBER_ROLE_UPDATED]: {
    icon: 'ph ph-user-gear',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Page member role updated',
    url: null,
    urlResolver: resolvePageRoutes
  },
  [NOTIFICATION_TYPES.PAGE_MEMBER_INVITE_ACCEPTED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Page team invitation accepted',
    url: null,
    urlResolver: resolvePageRoutes
  },
  [NOTIFICATION_TYPES.PAGE_MEMBER_INVITE_DECLINED]: {
    icon: 'ph ph-x-circle',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.PAGE,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Page team invitation declined',
    url: null,
    urlResolver: resolvePageRoutes
  },
  [NOTIFICATION_TYPES.BRONZE_BADGE]: {
    icon: 'ph ph-medal',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.ACHIEVEMENT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Badge bronze obtenu',
    url: null,
    urlResolver: resolveProfileBadgeRoutes
  },
  [NOTIFICATION_TYPES.SILVER_BADGE]: {
    icon: 'ph ph-medal',
    color: 'secondary',
    category: NOTIFICATION_CATEGORIES.ACHIEVEMENT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Badge argent obtenu',
    url: null,
    urlResolver: resolveProfileBadgeRoutes
  },
  [NOTIFICATION_TYPES.GOLD_BADGE]: {
    icon: 'ph ph-medal',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.ACHIEVEMENT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Badge or obtenu',
    url: null,
    urlResolver: resolveProfileBadgeRoutes
  },
  [NOTIFICATION_TYPES.VOICE_CALL]: {
    icon: 'ph ph-phone-call',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.CALL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Appel vocal entrant',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.VIDEO_CALL]: {
    icon: 'ph ph-video-camera',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.CALL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Appel vidéo entrant',
    url: null,
    urlResolver: resolveConversationRoutes
  },
  [NOTIFICATION_TYPES.SYSTEM_ANNOUNCEMENT]: {
    icon: 'ph ph-bell',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'System announcement',
    url: null,
    urlResolver: null
  },
  [NOTIFICATION_TYPES.BIRTHDAY_REMINDER]: {
    icon: 'ph ph-cake',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: "Rappel d'anniversaire",
    url: null,
    urlResolver: resolveBirthdayRoutes
  },
  [NOTIFICATION_TYPES.TASK_ASSIGNED]: {
    icon: 'ph ph-user-check',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Tâche assignée',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_COMPLETED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Tâche complétée',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_REOPENED]: {
    icon: 'ph ph-arrow-counter-clockwise',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Tâche réouverte',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.PROJECT_INVITATION]: {
    icon: 'ph ph-envelope-simple',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Invitation à un projet',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_UPDATED]: {
    icon: 'ph ph-pencil-simple',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Tâche mise à jour',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_REMINDER_DAILY]: {
    icon: 'ph ph-bell-ringing',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Rappel quotidien de tâche',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_REMINDER_BEFORE_DEADLINE]: {
    icon: 'ph ph-alarm',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Rappel avant échéance',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  [NOTIFICATION_TYPES.TASK_REMINDER_CUSTOM]: {
    icon: 'ph ph-clock',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.TODO,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Rappel personnalisé',
    url: null,
    urlResolver: resolveTodoRoutes
  },
  // Moderation notifications
  [NOTIFICATION_TYPES.POST_PENDING_MODERATION]: {
    icon: 'ph ph-clock',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Post en attente de modération',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.POST_APPROVED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Post approuvé',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.POST_BANNED]: {
    icon: 'ph ph-x-circle',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Post banni',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.STORY_PENDING_MODERATION]: {
    icon: 'ph ph-clock',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Story en attente de modération',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.STORY_APPROVED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Story approuvée',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.STORY_BANNED]: {
    icon: 'ph ph-x-circle',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Story bannie',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.COMMENT_PENDING_MODERATION]: {
    icon: 'ph ph-clock',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Commentaire en attente de modération',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.COMMENT_APPROVED]: {
    icon: 'ph ph-check-circle',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Commentaire approuvé',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.COMMENT_BANNED]: {
    icon: 'ph ph-x-circle',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Commentaire banni',
    url: null,
    urlResolver: resolvePostRoutes
  },
  [NOTIFICATION_TYPES.STORY_TAGGED]: {
    icon: 'ph ph-user-circle',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Vous avez été tagué dans une story',
    url: null,
    urlResolver: resolveStoryRoutes
  },
  [NOTIFICATION_TYPES.STORY_GROUP_TAGGED]: {
    icon: 'ph ph-user-circle',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Mentionné dans une story de groupe',
    url: null,
    urlResolver: resolveStoryRoutes
  },
  [NOTIFICATION_TYPES.STORY_PAGE_TAGGED]: {
    icon: 'ph ph-user-circle',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Mentionné dans une story de page',
    url: null,
    urlResolver: resolveStoryRoutes
  },
  [NOTIFICATION_TYPES.STORY_REACTION]: {
    icon: 'ph ph-heart',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Réaction sur votre story',
    url: null,
    urlResolver: resolveStoryRoutes
  },
  // Social Features
  [NOTIFICATION_TYPES.FAMILY_REQUEST]: {
    icon: 'ph ph-users',
    color: 'primary',
    category: NOTIFICATION_CATEGORIES.FRIENDSHIP,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Demande de famille',
    url: null,
    urlResolver: resolveFamilyRoutes
  },
  [NOTIFICATION_TYPES.CAMPAIGN_FINISHED]: {
    icon: 'ph ph-flag',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Campagne terminée',
    url: null,
    urlResolver: resolveCampaignRoutes
  },
  // Sports / Favorite Teams
  [NOTIFICATION_TYPES.FAVORITE_TEAM_MATCH_STARTING]: {
    icon: 'ph ph-clock',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Le match de votre équipe favorite commence bientôt',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_MATCH_STARTED]: {
    icon: 'ph ph-play',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Le match de votre équipe favorite a commencé',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_MATCH_FINISHED]: {
    icon: 'ph ph-check-circle',
    color: 'info',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Le match de votre équipe favorite est terminé',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_SCORED]: {
    icon: 'ph ph-flag',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Votre équipe favorite a marqué',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_WON]: {
    icon: 'ph ph-trophy',
    color: 'success',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Votre équipe favorite a gagné',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_LOST]: {
    icon: 'ph ph-x-circle',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Votre équipe favorite a perdu',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_DREW]: {
    icon: 'ph ph-minus-circle',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.MEDIUM,
    autoRead: false,
    description: 'Votre équipe favorite a fait match nul',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.FAVORITE_TEAM_MATCH_REMINDER]: {
    icon: 'ph ph-bell',
    color: 'warning',
    category: NOTIFICATION_CATEGORIES.OTHER,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Rappel: Match de votre équipe favorite',
    url: null,
    urlResolver: resolveSportsRoutes
  },
  [NOTIFICATION_TYPES.USER_WENT_LIVE]: {
    icon: 'ph ph-broadcast',
    color: 'danger',
    category: NOTIFICATION_CATEGORIES.SOCIAL,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    autoRead: false,
    description: 'Un utilisateur a commencé un live',
    url: null,
    urlResolver: resolveLiveRoutes
  }
};

/**
 * Utilitaires pour les types de notifications
 */
export const NotificationTypeUtils = {
  /**
   * Obtient la configuration d'un type de notification
   * @param {string} type - Type de notification
   * @returns {Object} Configuration du type
   */
  getConfig(type) {
    return NOTIFICATION_TYPE_CONFIG[type] || {
      icon: 'ph ph-bell',
      color: 'primary',
      category: NOTIFICATION_CATEGORIES.OTHER,
      priority: NOTIFICATION_PRIORITIES.MEDIUM,
      autoRead: false,
      description: 'Notification',
      url: null
    };
  },

  /**
   * Vérifie si un type de notification existe
   * @param {string} type - Type de notification
   * @returns {boolean} True si le type existe
   */
  isValidType(type) {
    return Object.values(NOTIFICATION_TYPES).includes(type);
  },

  /**
   * Obtient tous les types d'une catégorie
   * @param {string} category - Catégorie
   * @returns {Array} Types de la catégorie
   */
  getTypesByCategory(category) {
    return Object.entries(NOTIFICATION_TYPE_CONFIG)
      .filter(([_, config]) => config.category === category)
      .map(([type]) => type);
  },

  /**
   * Obtient tous les types d'une priorité
   * @param {string} priority - Priorité
   * @returns {Array} Types de la priorité
   */
  getTypesByPriority(priority) {
    return Object.entries(NOTIFICATION_TYPE_CONFIG)
      .filter(([_, config]) => config.priority === priority)
      .map(([type]) => type);
  },

  /**
   * Vérifie si une notification doit être marquée comme lue automatiquement
   * @param {string} type - Type de notification
   * @returns {boolean} True si auto-read
   */
  shouldAutoRead(type) {
    const config = this.getConfig(type);
    return config.autoRead;
  },

  /**
   * Retourne les destinations de navigation potentielles pour une notification
   * @param {Object} notification - Notification complète
   * @returns {Array} Liste de routes ou chemins
   */
  getNavigationTargets(notification) {
    if (!notification || !notification.type) {
      return [];
    }

    const config = this.getConfig(notification.type);
    const targets = [];

    targets.push(...toArray(config.url));

    if (typeof config.urlResolver === 'function') {
      const resolved = config.urlResolver(notification);
      targets.push(...toArray(resolved));
    }

    return targets;
  }
};

